import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MeasureChartComponentData } from "@core/data/types/advanced-report";
import { Snackbar } from "@core/material";
import { Select, Store } from "@ngxs/store";
import { Observable, ReplaySubject, Subject } from "rxjs";
import { InitReportChartForm, ReportChartFormState, ReportChartFormStateModel } from "./report-chart-edit-form.state";
import { Library, Measurement } from "@library";
import { takeUntil } from "rxjs/operators";
import { ChartCompilationType, ChartContextType, ChartDataDate, ChartDateType, CultevaChartTypes, SearchGroup, Status } from "@core/data";
import { getDefaultChartConfig, setChartConfigOptions } from "@core/data/types/Reports/report-charts";
import { LIBRARY } from "@app/advanced-report/library";

export interface ReportChartEditFormDialogData {
    reportKey: string;
    ownerOrgKey: string;
    optionKey: string;
    data: MeasureChartComponentData;
}

interface ChartOption {
    value: string,
    label: string,
    short: string,
    icon: string,
}

@Component({
    selector: 'pv-report-chart-edit-form-dialog',
    templateUrl: 'report-chart-edit-form.dialog.html',
    styleUrls:   ['report-chart-edit-form.dialog.scss'],
    preserveWhitespaces: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'pv-report-chart-edit-form-dialog',
    }
})
export class ReportChartEditFormDialog implements OnInit, OnDestroy {

    @Select(ReportChartFormState)
    state$: Observable<ReportChartFormStateModel>;

    @Select(ReportChartFormState.measurements)
    measures$: Observable<SearchGroup<Measurement>[]>;

    readonly Status = Status;

    public measureGroupsFilterCtrl: FormControl = new FormControl();
    public secondaryMeasureGroupsFilterCtrl: FormControl = new FormControl();
    public tertiaryMeasureGroupsFilterCtrl: FormControl = new FormControl();

    public filteredMeasureGroups: ReplaySubject<SearchGroup<Measurement>[]> = new ReplaySubject<SearchGroup<Measurement>[]>(1);
    public filteredSecondaryMeasureGroups: ReplaySubject<SearchGroup<Measurement>[]> = new ReplaySubject<SearchGroup<Measurement>[]>(1);
    public filteredTertiaryMeasureGroups: ReplaySubject<SearchGroup<Measurement>[]> = new ReplaySubject<SearchGroup<Measurement>[]>(1);

    protected _onDestroyMeas = new Subject<void>();
    protected _onDestroySecondary = new Subject<void>();
    protected _onDestroyTertiary = new Subject<void>();

    labelControl: FormControl = new FormControl(null);
    chartTitleControl: FormControl = new FormControl(null);
    typeControl: FormControl = new FormControl('bar', [Validators.required]);
    measureControl: FormControl = new FormControl(null, [Validators.required]);
    contextControl: FormControl = new FormControl(null, [Validators.required]);
    dateFormatControl: FormControl = new FormControl(null, [Validators.required]);
    compilationControl: FormControl = new FormControl(null, [Validators.required]);
    secondaryMeasureControl: FormControl = new FormControl(null);
    tertiaryMeasureControl: FormControl = new FormControl(null);
    dataDateControl: FormControl = new FormControl(null, [Validators.required]);
    configControl: FormControl = new FormControl(null, [Validators.required]);

    formGroup: FormGroup = new FormGroup({
        label: this.labelControl,
        chartTitle: this.chartTitleControl,
        type: this.typeControl,
        context: this.contextControl,
        dateFormat: this.dateFormatControl,
        compilationType: this.compilationControl,
        measure: this.measureControl,
        secondaryMeasure: this.secondaryMeasureControl,
        tertiaryMeasure: this.tertiaryMeasureControl,
        dataDate: this.dataDateControl,
        chartConfig: this.configControl,
    });

    //TODO ADD ChartXaxisOption CONTROLS AND OPTIONS

    //? OPTIONS
    measurementGroups: SearchGroup<Measurement>[];
    chartTypeOptions: ChartOption[];

    chartDateOptions = [
        {value: ChartDateType.DAY, label: 'Day'},
        {value: ChartDateType.WEEK, label: 'Week'},
        {value: ChartDateType.YEAR, label: 'Year'},
    ];

    chartContextOptions = [
        {value: ChartContextType.CULTIVAR, label: 'Cultivar'},
        {value: ChartContextType.SITE, label: 'Site'},
        {value: ChartContextType.SAMPLE, label: 'Sample'},
        {value: ChartContextType.EVALUATION, label: 'Evaluation'},
    ];

    chartCompilationOptions = [
        {value: ChartCompilationType.COMPARISON, label: 'Comparison'},
        {value: ChartCompilationType.SUMMARY, label: 'Summary'},
    ];

    chartDataDateOptions = [
        { value: ChartDataDate.EVALUATION, label: 'Evaluation Start Date' },
        { value: ChartDataDate.MEASUREMENT, label: 'Measurement Date' },
    ]

    chartTypeRegistry: Partial<CultevaChartTypes> = {
        bar: null,
        bubble: null,
        line: null,
        scatter: null,
        boxplot: null,
    };

    //? LABELS
    xAxisLabel: string = null;
    yAxisLabel: string = null;

    private _destroy$ = new Subject();

    constructor(
        @Inject(LIBRARY) private _library: Library,
        private _dialogRef: MatDialogRef<ReportChartEditFormDialogData>,
        @Inject(MAT_DIALOG_DATA) private _data: ReportChartEditFormDialogData,
        private _snackbar: Snackbar,
        private _store: Store,
    ) {}

    ngOnInit() {
        const { reportKey, ownerOrgKey, data, optionKey } = this._data;

        this._store.dispatch(new InitReportChartForm(reportKey, ownerOrgKey, optionKey, data));

        this.measures$.pipe(takeUntil(this._destroy$))
            .subscribe(measures => {
                if (!measures) return;

                this.resetMeasures(measures);
                this.resetStaticControls(data);
            });

        this.typeControl.valueChanges
            .subscribe((data: keyof CultevaChartTypes) => {
                this.secondaryMeasureControl.setValidators([]);
                this.tertiaryMeasureControl.setValidators([]);

                //? Reset Scatter Controls
                if (data !== 'scatter' && data !== 'bubble') this.resetScatterControls();

                if (data === 'boxplot') {
                    this.dateFormatControl.setValue(ChartDateType.ALL);
                    if (!this.compilationControl.value) this.compilationControl.setValue(ChartCompilationType.SUMMARY);
                    return;
                }

                if (data === 'scatter' || data === 'bubble') {
                    this.dateFormatControl.setValue(ChartDateType.ALL);
                    if (data === 'scatter') this.compilationControl.setValue(ChartCompilationType.COMPARISON);

                    this.resetScatterControls();

                    this.secondaryMeasureControl.setValidators([Validators.required]);

                    if (data === 'bubble') this.tertiaryMeasureControl.setValidators([Validators.required]);

                    return;
                }

                if (this.dateFormatControl.value === ChartDateType.ALL) this.dateFormatControl.setValue(ChartDateType.WEEK);
            });

        this.measureGroupsFilterCtrl.valueChanges
            .pipe(takeUntil(this._onDestroyMeas))
            .subscribe(() => {
                this.filterMeasureGroups(this.measureGroupsFilterCtrl, this.filteredMeasureGroups);
            });

        this.secondaryMeasureGroupsFilterCtrl.valueChanges
            .pipe(takeUntil(this._onDestroySecondary))
            .subscribe(() => {
                this.filterMeasureGroups(this.secondaryMeasureGroupsFilterCtrl, this.filteredSecondaryMeasureGroups, 1);
            })

        this.tertiaryMeasureGroupsFilterCtrl.valueChanges
            .pipe(takeUntil(this._onDestroyTertiary))
            .subscribe(() => {
                this.filterMeasureGroups(this.tertiaryMeasureGroupsFilterCtrl, this.filteredTertiaryMeasureGroups, 2);
            })

        this.measureControl.valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {

                this.secondaryMeasureControl.patchValue(null);
                this.tertiaryMeasureControl.patchValue(null);

                this.filterMeasureGroups(this.secondaryMeasureGroupsFilterCtrl, this.filteredSecondaryMeasureGroups, 1);
                this.filterMeasureGroups(this.tertiaryMeasureGroupsFilterCtrl, this.filteredTertiaryMeasureGroups, 2);
            })

        this.secondaryMeasureControl.valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {

                this.tertiaryMeasureControl.patchValue(null);

                this.filterMeasureGroups(this.tertiaryMeasureGroupsFilterCtrl, this.filteredTertiaryMeasureGroups, 2);
            });

        this.tertiaryMeasureControl.valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {

                this.filterMeasureGroups(this.secondaryMeasureGroupsFilterCtrl, this.filteredSecondaryMeasureGroups, 1);
            });

        this.chartTypeOptions = Object.keys(this.chartTypeRegistry).map((option) => {
            let label = this.toTitleCase(option);
            let short = label;
            if (option === 'boxplot') {
                label = 'Standard Deviation Candlestick';
                short = 'SD Candlestick';
            }
            return { value: option, label: this.toTitleCase(label), short, icon: this.getChartIconString(option) }
        });
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();

        this._onDestroyMeas.next();
        this._onDestroyMeas.complete();

        this._onDestroySecondary.next();
        this._onDestroySecondary.complete();

        this._onDestroyTertiary.next();
        this._onDestroyTertiary.complete();
    }

    resetMeasures(measures: SearchGroup<Measurement>[]) {
        this.measurementGroups = this.cloneData(measures);
        this.filteredMeasureGroups.next(this.cloneData(measures))
        this.filteredSecondaryMeasureGroups.next(this.filterActiveMeasures(this.cloneData(measures), [this.measureControl.value, this.tertiaryMeasureControl.value]))
        this.filteredTertiaryMeasureGroups.next(this.filterActiveMeasures(this.cloneData(measures), [this.measureControl.value, this.secondaryMeasureControl.value]))
    }

    filterMeasureGroups(filterCtrl: FormControl, subject: ReplaySubject<SearchGroup<Measurement>[]>, dataPointer: number = 0) {
        if (!this.measurementGroups) return;

        let search = filterCtrl.value;
        let dataCopy: SearchGroup<Measurement>[] = this.cloneData(this.measurementGroups);

        if (dataPointer == 1) dataCopy = this.filterActiveMeasures(dataCopy, [this.measureControl.value, this.tertiaryMeasureControl.value]);
        if (dataPointer == 2) dataCopy = this.filterActiveMeasures(dataCopy, [this.measureControl.value, this.secondaryMeasureControl.value]);

        if (!search) return subject.next(dataCopy);

        search = search.toLowerCase();

        subject.next(
            dataCopy.filter(measureGroup => {
                const showGroup = measureGroup.id.indexOf(search) > -1;
                if (!showGroup) {
                    measureGroup.data = measureGroup.data.filter(meas => meas.label.toLowerCase().indexOf(search) > -1);
                }

                return measureGroup.data.length > 0;
            })
        );
    }

    filterActiveMeasures(data: SearchGroup<Measurement>[], measureIds: string[]): SearchGroup<Measurement>[] {
        return data.map(group => ({
            ...group,
            data: group.data.filter(meas => !measureIds.includes(meas.id)),
        }));
    }

    resetScatterControls() {
        this.secondaryMeasureControl.reset(null);
        this.tertiaryMeasureControl.reset(null);
    }

    resetStaticControls(data: MeasureChartComponentData) {
        this.labelControl.reset(data.label || null, { emitEvent: false });
        this.typeControl.reset(data.type || 'bar', { emitEvent: false });
        this.contextControl.reset(data.context || null, { emitEvent: false });
        this.dateFormatControl.reset(data.dateFormat || null, { emitEvent: false });
        this.compilationControl.reset(data.compilationType || null, { emitEvent: false });
        this.measureControl.reset(data.measure || null, { emitEvent: false });
        this.secondaryMeasureControl.reset(data.secondaryMeasure || null, { emitEvent: false });
        this.tertiaryMeasureControl.reset(data.tertiaryMeasure || null, { emitEvent: false });
        this.dataDateControl.reset(data.dataDate || ChartDataDate.EVALUATION, { emitEvent: false });
    }

    patchChartConfig() {
        this.configControl.patchValue(this.getChartConfig());
    }

    getChartConfig() {
        return getDefaultChartConfig(this.typeControl.value);
    }

    setChartLabels(chartType: keyof CultevaChartTypes) {
        let measure: Measurement = this.getMeasureWithId(this.measureControl.value);
        let secondaryMeasure: Measurement = this.getMeasureWithId(this.secondaryMeasureControl.value);
        let tertiaryMeasure: Measurement = this.getMeasureWithId(this.tertiaryMeasureControl.value);

        if (chartType == 'scatter') {
            this.xAxisLabel = `${measure.label} [${measure.unit}]`;
            this.yAxisLabel = `${secondaryMeasure.label} [${secondaryMeasure.unit}]`;
            return;
        }

        if (chartType == 'bubble') {
            this.xAxisLabel = `${measure.label} [${measure.unit}]`;
            this.yAxisLabel = `${secondaryMeasure.label} [${secondaryMeasure.unit}]`;
            this.chartTitleControl.setValue(`Scatter Plot of ${measure.label} and ${secondaryMeasure.label} with ${tertiaryMeasure.label} radius`);
            return;
        }

        this.xAxisLabel = this.getChartName(measure);
        this.yAxisLabel = `${measure.label} [${measure.unit}]`
    }

    getMeasureWithId(measureId: string) {
        return this._library.measures.get(measureId);
    }

    setFormValue(control: FormControl, value: any) {
        control.patchValue(value);
    }

    trackById(index, item){
        return item.id;
    }

    attempt() {
        this.patchChartConfig();

        this.formGroup.updateValueAndValidity();
        this.formGroup.markAsDirty();
        this.formGroup.markAllAsTouched();

        if (!this.formGroup.valid) return this._snackbar.error('Invalid data, check your inputs and try again.');

        const {
            label,
            chartTitle,
            type,
            context,
            dateFormat,
            compilationType,
            measure,
            secondaryMeasure,
            tertiaryMeasure,
            dataDate,
            chartConfig,
        } = this.formGroup.value;

        this.setChartLabels(type);

        let displayLabel = type === 'bubble' ? chartTitle : label;

        const data: MeasureChartComponentData = {
            ...this._data.data,
            label,
            chartTitle,
            type,
            context,
            dateFormat,
            compilationType,
            measure,
            secondaryMeasure,
            tertiaryMeasure,
            dataDate,
            chartConfig: {
                ...setChartConfigOptions(type, chartConfig, this.xAxisLabel, this.yAxisLabel, displayLabel)
            }
        };



        this._dialogRef.close({
            status: true,
            data: data,
        });
    }

    cancel() {
        this._dialogRef.close({status: false});
    }

    private getChartIconString(id: string) {
        if (id === "boxplot") return "candlestick_chart"
        if (id === "bubble") return "bubble_chart"
        if (id === "scatter") return "scatter_plot"
        if (id === "line") return "line_axis"

        return "bar_chart_4_bars"
    }

    private toTitleCase(str: string): string {
        return str.replace(
          /\w\S*/g,
          text => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase()
        );
    }

    private cloneData(data: any) {
        return JSON.parse(JSON.stringify(data));
    }

    private getChartName(measure: Measurement,): string {
        const type = this.typeControl.value;
        const context = this.contextControl.value;
        const dateFormat = this.dateFormatControl.value;
        const compilationType = this.compilationControl.value;

        if (type === 'boxplot') return `${this.toTitleCase(context)} ${measure.label} breakdown`;

        let date = this.toTitleCase(dateFormat);
        let compilation = '';
        let suffix = '';

        if (compilationType === ChartCompilationType.COMPARISON) {
            compilation = 'Synced ';
            suffix = ' since ';

            suffix += 'origin';
        }

        let chartName = `${compilation}Avg. ${measure.label} per ${date}${suffix}`;

        return chartName;
    }
}